"use client";

import { actionsMap } from "@actions-map";
import { AppProvider, PwaInstallWatcher, RouterProvider } from "@plt-bet/blocks";
import { UnleashProvider } from "@plt-bet/feature-flags-utils";
import { usePathname, useRouter } from "next/navigation";
import { PropsWithChildren, useEffect } from "react";
import { appConfig } from "@/configs";
import { useLanguage } from "@/hooks";
import { useLanguageRedirect } from "@/hooks";
import staticFeatureFlags from "@/static-feature-flags.js";
import { FeatureFlags } from "@/types/feature-flags";

/**
 * Registers the shared service worker (used by web-push AND PWA install).
 * The SW must be active for browsers to fire `beforeinstallprompt`, so we
 * register it eagerly on mount — regardless of push subscription flow.
 */
const registerPwaServiceWorker = () => {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  // Fire and forget — errors are non-fatal for the app.
  navigator.serviceWorker.register("/sw.js").catch(err => {
    console.warn("[pwa] service worker registration failed", err);
  });
};
export function LayoutRender({
  children
}: PropsWithChildren) {
  const {
    push,
    back
  } = useRouter();
  const pathname = usePathname();
  const {
    language,
    changeLanguage
  } = useLanguage();
  useEffect(() => {
    if (language) {
      document.documentElement.lang = language;
    }
  }, [language]);
  useEffect(() => {
    registerPwaServiceWorker();
  }, []);
  useLanguageRedirect();
  const content = <RouterProvider push={(path: string) => push(`/${language}${path}`)} back={back} pathname={pathname}>
      <AppProvider appId={process.env.NEXT_PUBLIC_APP_ID} projectId={process.env.NEXT_PUBLIC_PROJECT_ID} clientId={process.env.NEXT_PUBLIC_CLIENT_ID} appConfig={appConfig} currentLanguage={language} environment={process.env.NEXT_PUBLIC_ENVIRONMENT} apiBaseConfig={{
      b2cBaseUrl: process.env.NEXT_PUBLIC_B2C_BASE_URL,
      b2bBaseUrlV2: `${process.env.NEXT_PUBLIC_B2B_BASE_URL}/api/v2`,
      b2bBaseUrl: `${process.env.NEXT_PUBLIC_B2B_BASE_URL}/api/v1`
    }} webPushConfig={{
      pushBackendUrl: process.env.NEXT_PUBLIC_PUSH_BACKEND_URL,
      vapidPublicKey: process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY
    }} withAuth actionsMap={actionsMap} onLanguageChange={changeLanguage}>
        <PwaInstallWatcher />
        {children}
      </AppProvider>
    </RouterProvider>;
  if (process.env.NEXT_PUBLIC_UNLEASH_PROJECT_URL && process.env.NEXT_PUBLIC_UNLEASH_CLIENT_KEY) {
    return <UnleashProvider config={{
      url: process.env.NEXT_PUBLIC_UNLEASH_PROJECT_URL,
      clientKey: process.env.NEXT_PUBLIC_UNLEASH_CLIENT_KEY,
      refreshInterval: 0,
      appName: "site-builder"
    }} staticFlags={staticFeatureFlags as FeatureFlags}>
        {content}
      </UnleashProvider>;
  }
  return content;
}